import { FilterFacets } from '@/study-search/domain';
import { ParameterType } from '@/study-search/enums';
import universitiesData from './data/top-universities.json';

import { IFaq } from '@/study-search/interfaces';
import getters from '@/study-search/store/getters';
import { FaqFacetsHandler } from './FaqFacetsHandler';
import { Filter } from '../filter';

export class CountryFaqHandler extends FaqFacetsHandler {
	dataStorageKey = 'faqs/country';

	protected canHandle(): boolean {
		return this.indexableFilters.length >= 1 && this.indexableFilters.some((_) => _.type === ParameterType.COUNTRY);
	}

	protected async handle(): Promise<IFaq[]> {
		const filterOption = this.getFilterOption(ParameterType.COUNTRY);

		if (!filterOption) {
			throw new Error('Filter option not found.');
		}

		const countryName = filterOption.optionTitle;
		const topUniversities = this.getTopUniversities(filterOption.optionValue);
		const searchPageUrl = this.getSearchPageOnlineProgrammesUrl(`/${filterOption.optionPath}`);
		const articleUrl = this.getArticleUrl();

		let onlineProgrammeCount: number, percentageOfOnlineStudies: string;
		const data = this.getFromDataStorage();

		if (!data) {
			const countryFacets = await this.getFacetsForFilterCombination();

			onlineProgrammeCount = this.getOnlineProgrammeCount(countryFacets);
			percentageOfOnlineStudies = this.getPercentageOfOnlineStudies(countryFacets, onlineProgrammeCount);

			this.storeOnlineProgrammeData({ onlineProgrammeCount, percentageOfOnlineStudies });
		} else {
			onlineProgrammeCount = data.onlineProgrammeCount;
			percentageOfOnlineStudies = data.percentageOfOnlineStudies;
		}

		const faqs = [
			{
				question: `How to apply for a Master's degree in ${countryName}?`,
				answer: `<div>Submit an application via UCAS Postgraduate or directly to the university.
				Before you do, make sure you meet the entry requirements and provide transcripts, 
				a <a href="${articleUrl}">personal statement</a>, and reference letters.</div>`,
			},
			{
				question: `Can I get a Master's degree in ${countryName} online?`,
				answer: `<div>Yes, you can. There are <a href="${searchPageUrl}">${onlineProgrammeCount} online Master’s in ${countryName}</a>,
				representing ${percentageOfOnlineStudies}% of the country’s programmes listed on Mastersportal.</div>`,
			},
		];

		if (topUniversities.length > 0) {
			const universitiesToString = this.getTopUniversitiesToString(topUniversities);
			const metaRankingUrl = this.getMetaRankingUrl(filterOption.optionPath);

			faqs.push({
				question: `What are the top universities in ${countryName}?`,
				answer: `<div>Some of the best-known and most reputable universities in ${countryName} ${universitiesToString}.
					<a href="${metaRankingUrl}">Studyportals Meta Rankings lists all prestigious universities in ${countryName}</a>.</div>`,
			});
		}

		return faqs;
	}

	async getFacetsForFilterCombination(): Promise<FilterFacets[]> {
		const filters = getters.getFilters();

		const filtersCopy = filters.map((_) => ({ ..._ })) as Filter[];
		filtersCopy.forEach((_) => {
			if (_.type !== ParameterType.COUNTRY) {
				_.values = [];
			}
		});

		const parameters = this.parameterService.getParameters(filtersCopy);

		const facetsResult = await this.getFacets(parameters);

		return facetsResult;
	}

	private getTopUniversities(countryId: string): string[] {
		const entry = universitiesData.find((_) => _.id === Number(countryId));

		return entry?.top_universities || [];
	}

	private getTopUniversitiesToString(universities: string[]): string {
		return universities.length === 1
			? ` is ${universities[0]}`
			: ` are ${universities.slice(0, -1).join(', ')}, and ${universities.slice(-1)}`;
	}

	private getMetaRankingUrl(countryPath: string): string {
		const hostName = this.getHostname();

		return hostName ? `${hostName}/university-rankings/${countryPath}` : '/';
	}

	private getArticleUrl(): string {
		const hostName = this.getHostname();

		return hostName ? `${hostName}/articles/2030/writing-the-perfect-personal-statement-for-your-masters-or-phd-application.html` : '/';
	}
}
